import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { resendVerificationEmail, isAuthenticated, getUserType } from '../api/api';
import { getPostAuthPath } from '../config/productFlags';
import './Auth.css';

function VerifyEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [userType, setUserType] = useState(null);
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loggedInUserType, setLoggedInUserType] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    const typeParam = params.get('type');
    
    if (statusParam) {
      setStatus(statusParam);
      setUserType(typeParam);
    }
    
    // Check if user is already logged in
    setIsLoggedIn(isAuthenticated());
    setLoggedInUserType(getUserType());
    
    // Get email and userType from location state if available
    if (location.state?.email) {
      setEmail(location.state.email);
    }
    if (location.state?.userType) {
      setUserType(location.state.userType);
    }
  }, [location]);

  // Get the correct dashboard path based on user type
  const getDashboardPath = () => {
    const type = loggedInUserType || userType || localStorage.getItem('userType');
    return getPostAuthPath(type);
  };

  const handleSkip = () => {
    navigate(getDashboardPath());
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    if (resendCooldown > 0) {
      setError(`Please wait ${resendCooldown} seconds before resending`);
      return;
    }

    setResending(true);
    setError('');
    setResendSuccess(false);

    try {
      await resendVerificationEmail(email);
      setResendSuccess(true);
      setResendCooldown(60); // 60 second cooldown
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const renderContent = () => {
    if (status === 'success') {
      // Determine the correct dashboard based on logged-in status or verification type
      const dashboardPath = isLoggedIn 
        ? getPostAuthPath(loggedInUserType)
        : getPostAuthPath(userType);
      
      return (
        <div className="verify-success">
          <div className="verify-icon success">✓</div>
          <h1>Email Verified!</h1>
          <p>Your email has been successfully verified. You can now access all features of Milan AI.</p>
          
          {isLoggedIn ? (
            // User is logged in - take them directly to dashboard
            <Link to={dashboardPath} className="auth-btn">
              Go to Dashboard
            </Link>
          ) : (
            // User is not logged in - they need to sign in first
            <>
              <Link to="/login" className="auth-btn">
                Sign In to Continue
              </Link>
              <p className="verify-hint">
                Sign in to access your {userType === 'brand' ? 'Brand' : 'Creator'} Dashboard
              </p>
            </>
          )}
        </div>
      );
    }

    if (status === 'expired') {
      return (
        <div className="verify-expired">
          <div className="verify-icon warning">⏱</div>
          <h1>Link Expired</h1>
          <p>Your verification link has expired. Please request a new one below.</p>
          
          <form onSubmit={handleResend} className="resend-form">
            {error && <div className="error-message">{error}</div>}
            {resendSuccess && (
              <div className="success-message">
                Verification email sent! Please check your inbox.
              </div>
            )}
            <div className="form-group">
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="auth-btn" disabled={resending || resendCooldown > 0}>
              {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Verification Email'}
            </button>
          </form>
        </div>
      );
    }

    if (status === 'invalid') {
      return (
        <div className="verify-invalid">
          <div className="verify-icon error">✕</div>
          <h1>Invalid Link</h1>
          <p>This verification link is invalid or has already been used.</p>
          
          <form onSubmit={handleResend} className="resend-form">
            {error && <div className="error-message">{error}</div>}
            {resendSuccess && (
              <div className="success-message">
                Verification email sent! Please check your inbox.
              </div>
            )}
            <div className="form-group">
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="auth-btn" disabled={resending || resendCooldown > 0}>
              {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Request New Verification Link'}
            </button>
          </form>
          
          <p className="verify-hint">
            Already verified? <Link to="/login">Sign in here</Link>
          </p>
        </div>
      );
    }

    // Default: Show resend form (for users who land here directly)
    return (
      <div className="verify-default">
        <div className="verify-icon info">✉</div>
        <h1>Verify Your Email</h1>
        <p>
          We've sent a verification link to your email address. 
          Please click the link to verify your account.
        </p>
        
        <div className="verify-instructions">
          <h4>Didn't receive the email?</h4>
          <ul>
            <li>Check your spam or junk folder</li>
            <li>Make sure you entered the correct email</li>
            <li>Wait a few minutes and try again</li>
          </ul>
        </div>
        
        <form onSubmit={handleResend} className="resend-form">
          {error && <div className="error-message">{error}</div>}
          {resendSuccess && (
            <div className="success-message">
              Verification email sent! Please check your inbox.
            </div>
          )}
          <div className="form-group">
            <input
              type="email"
              placeholder="Enter your email to resend"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{ color: '#000000' }}
            />
          </div>
          <button type="submit" className="auth-btn secondary" disabled={resending || resendCooldown > 0}>
            {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Verification Email'}
          </button>
        </form>
        
        <div className="verify-skip-section">
          <p className="skip-text">You can verify your email later</p>
          <button onClick={handleSkip} className="skip-btn">
            Skip for now → Go to Dashboard
          </button>
        </div>
        
        <p className="verify-hint">
          Already verified? <Link to="/login">Sign in here</Link>
        </p>
      </div>
    );
  };

  return (
    <div className="auth-page verify-page">
      <div className="verify-container">
        <Link to="/" className="auth-logo">
          <img src="/logo.png" alt="Milan AI" className="logo-image" />
          <span className="logo-text">Milan AI</span>
        </Link>
        
        <div className="verify-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
